import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Skeleton,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
  LinearProgress,
  Collapse,
} from "@mui/material";
import { useQuery } from "react-query";
import { useStyles } from "./styles";
import { useAuth } from "../../hooks/AuthContext";
import { getLimitesDiponiveis } from "../../api/LimiteOperacional";
import theme from "../Paleta/theme";
import SouthEastOutlinedIcon from "@mui/icons-material/SouthEastOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import useAlert from "../../hooks/useAlert";
import { Link } from "react-router-dom";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { formatMoneyInString } from "../../util/helpers";
import { ButtonCustom } from "../Botoes/Button";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

export default function LimiteOperacional({
  handlerShowModal,
  isLoading = false,
}) {
  const [shouldShow, setShouldShow] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { user } = useAuth();
  const classes = useStyles();
  const [isLoadingLimite, setIsloadingLimite] = useState(false);
  const { showAlertError } = useAlert();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { data: limites, refetch } = useQuery(
    ["LIMITES"],
    () => getLimitesDiponiveis(user.id),
    {
      onSuccess: (data) => {
        if (!data || !data?.data) {
          showAlertError(
            "Ops, ocorreu um erro, informe ao suporte tecnico o seguinte erro: Erro ao Buscar Limite Operacional - R453E "
          );
          return;
        }

        if (data.errorMessage) {
          showAlertError(
            `Ops, ocorreu um erro, informe ao suporte tecnico o seguinte erro: ${data.errorMessage} - R1800 `
          );
          return;
        }

        setIsloadingLimite(false);
      },
      onError: (error) => {
        showAlertError(
          `Ops, ocorreu um erro, informe ao suporte tecnico: ${error?.message} -  R19FE`
        );
      },
      refetchOnWindowFocus: false,
      enabled: false,
    }
  );

  useEffect(() => {
    setIsloadingLimite(true);
    refetch();
  }, []);

  const calculateUsagePercentage = (utilizado, total) => {
    if (!utilizado || !total || total === 0) return 0;
    return (utilizado / total) * 100;
  };

  const getProgressColor = (percentage) => {
    if (percentage < 50) return "success";
    if (percentage < 80) return "warning";
    return "error";
  };

  const LimiteCard = ({ row, isVisible }) => {
    const usagePercentage = isVisible
      ? calculateUsagePercentage(row.limiteUtilizado, row.limiteOperacional)
      : 0;

    return (
      <Card
        sx={{
          mb: 1.5,
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          border: "1px solid",
          borderColor: "divider",
          "&:last-child": {
            mb: 0,
          },
        }}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <AccountBalanceIcon
                sx={{ fontSize: 18, mr: 1, color: "primary.main" }}
              />
              <Typography variant="body2" fontWeight="medium">
                {row.mercado || "Câmbio"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Chip
                label={row.descricao}
                size="small"
                variant="outlined"
                sx={{ height: 24, fontSize: "0.7rem", mr: 1 }}
              />
              <IconButton
                size="small"
                onClick={() => setShouldShow(!shouldShow)}
                sx={{ color: "text.secondary" }}
              >
                {isVisible ? (
                  <VisibilityOffOutlinedIcon fontSize="small" />
                ) : (
                  <RemoveRedEyeOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <SouthEastOutlinedIcon
              fontSize="small"
              sx={{ mr: 0.5, color: "primary.main" }}
            />
            <Typography variant="body2" color="text.secondary">
              Utilizado:{" "}
              {isVisible
                ? formatMoneyInString(row.limiteUtilizado, row.simboloMoeda)
                : "••••••"}
            </Typography>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <LinearProgress
              variant="determinate"
              value={isVisible ? usagePercentage : 0}
              color={isVisible ? getProgressColor(usagePercentage) : "primary"}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: "grey.100",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Limite total
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {isVisible
                  ? formatMoneyInString(row.limiteOperacional, row.simboloMoeda)
                  : "••••••"}
              </Typography>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Disponível
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {isVisible
                  ? formatMoneyInString(row.limiteDisponivel, row.simboloMoeda)
                  : "••••••"}
              </Typography>
            </Box>
          </Box>

          {isVisible && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
              <Chip
                label={`${usagePercentage.toFixed(0)}% utilizado`}
                size="small"
                color={getProgressColor(usagePercentage)}
                variant="outlined"
                sx={{ height: 20, fontSize: "0.65rem" }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          <AccountBalanceIcon
            sx={{ fontSize: 20, mr: 3, color: "primary.main" }}
          />
          <Box>
            <Typography
              variant="h6"
              fontWeight="bold"
              color="text.primary"
              lineHeight={1.2}
            >
              Limites operacionais
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Saiba quanto ainda pode operar
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => setShouldShow(!shouldShow)}
            sx={{
              color: shouldShow ? "primary.main" : "text.secondary",
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            {shouldShow ? (
              <VisibilityOffOutlinedIcon fontSize="small" />
            ) : (
              <RemoveRedEyeOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={true} timeout="auto">
        {isLoadingLimite ? (
          <Box>
            {Array.from({ length: 2 }, (_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                animation="wave"
                height={120}
                sx={{ borderRadius: 2, mb: 1.5 }}
              />
            ))}
          </Box>
        ) : limites?.data && limites.data.length > 0 ? (
          <Box>
            {limites.data.map((row, index) => (
              <LimiteCard key={index} row={row} isVisible={shouldShow} />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 2,
              backgroundColor: "grey.50",
            }}
          >
            <AccountBalanceIcon
              sx={{ fontSize: 32, color: "text.secondary", mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              Nenhum limite operacional encontrado
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Link
            to="/ClienteLimiteOperacionalPlus"
            style={{ textDecoration: "none", width: "100%" }}
          >
            <ButtonCustom
              variant="outlined"
              fullWidth
              startIcon={<TrendingUpIcon />}
              sx={{
                borderRadius: 2,
                py:1,
                px:3,
                textTransform: "none",
                fontWeight: "medium",
                borderWidth: 1.5,
                "&:hover": {
                  borderWidth: 1.5,
                },
              }}
            >
              Solicitar aumento de limite
            </ButtonCustom>
          </Link>
        </Box>
      </Collapse>
    </Box>
  );
}
